/**
 * Thin fetch wrapper for /api calls.
 *
 * - Sends cookies (`credentials: include`) so the JWT cookie set by the
 *   OAuth callback flows automatically once M2 lands.
 * - On 401 with `code: NEED_REAUTH`, redirects the browser to
 *   `/api/auth/login?redirect=<current location>` so the user re-auths.
 *   (Until M2 ships, the redirect target won't exist — caller code will see
 *   the raw 401 in dev.)
 */

export class ApiError extends Error {
  status: number;
  code?: string;
  detail?: unknown;

  constructor(status: number, code: string | undefined, message: string, detail?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

async function parseError(resp: Response): Promise<ApiError> {
  let code: string | undefined;
  let message = resp.statusText || `HTTP ${resp.status}`;
  let detail: unknown;
  try {
    const body = (await resp.json()) as { error?: { code?: string; message?: string; detail?: unknown } };
    if (body?.error) {
      code = body.error.code;
      message = body.error.message ?? message;
      detail = body.error.detail;
    }
  } catch {
    // non-JSON body, keep defaults
  }
  return new ApiError(resp.status, code, message, detail);
}

/** 401 NEED_REAUTH 时跳 OAuth 登录。返回 true 表示已触发跳转。 */
function maybeRedirectToLogin(err: ApiError): boolean {
  if (err.status === 401 && err.code === "NEED_REAUTH") {
    const redirect = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/api/auth/login?redirect=${redirect}`;
    return true;
  }
  return false;
}

export async function apiGet<T>(path: string): Promise<T> {
  const resp = await fetch(path, { credentials: "include" });
  if (!resp.ok) {
    const err = await parseError(resp);
    if (maybeRedirectToLogin(err)) return new Promise<T>(() => {}); // 挂起，等跳转
    throw err;
  }
  return (await resp.json()) as T;
}

export async function apiSend<T>(
  path: string,
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  body?: unknown,
): Promise<T> {
  const resp = await fetch(path, {
    method,
    credentials: "include",
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!resp.ok) {
    const err = await parseError(resp);
    if (maybeRedirectToLogin(err)) return new Promise<T>(() => {}); // 挂起，等跳转
    throw err;
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}
